/**
 * Column metadata of the synced tables, derived from SYNC_TABLES (contracts/sync.ts). The generic
 * register writes of the store (store.ts) and the sync applier use it; nothing else hard-codes
 * which column belongs to which register.
 */

import { SYNC_TABLES, type SyncRegisterSpec, type SyncTableName, type SyncTableSpec } from '../contracts/sync.js';
import type { SqlValue } from '../contracts/storage.js';
import { DigesterError } from '../kernel/errors.js';

export interface TableMeta {
  readonly table: SyncTableName;
  readonly key: readonly string[];
  readonly registers: readonly SyncRegisterSpec[];
  /** Every column the sync layer knows: key + register columns + hlc columns. */
  readonly columns: readonly string[];
  /** Column -> register name, for data and hlc columns. */
  readonly registerOf: ReadonlyMap<string, string>;
  /** The register holding `deleted_at`, when the table has one. */
  readonly tombstoneRegister: string | null;
  readonly parentMessageColumn: string | null;
}

function build(spec: SyncTableSpec): TableMeta {
  const registerOf = new Map<string, string>();
  for (const r of spec.registers) {
    registerOf.set(r.hlc, r.name);
    for (const c of r.columns) registerOf.set(c, r.name);
  }
  const columns = [...spec.key, ...spec.registers.flatMap((r) => [...r.columns, r.hlc])];
  const tomb = spec.registers.find((r) => r.columns.includes('deleted_at'));
  return {
    table: spec.table as SyncTableName,
    key: spec.key,
    registers: spec.registers,
    columns,
    registerOf,
    tombstoneRegister: tomb ? tomb.name : null,
    parentMessageColumn: spec.parentMessageColumn ?? null,
  };
}

export const TABLE_META: ReadonlyMap<string, TableMeta> = new Map(
  (SYNC_TABLES as readonly SyncTableSpec[]).map((spec) => [spec.table, build(spec)]),
);

export function tableMeta(table: string): TableMeta {
  const meta = TABLE_META.get(table);
  if (!meta) throw new DigesterError('internal', `Not a synced table: ${table}`);
  return meta;
}

export function isSyncedTable(table: string): table is SyncTableName {
  return TABLE_META.has(table);
}

/** Key values -> the wire id (values joined with '|'). */
export function wireId(values: readonly SqlValue[]): string {
  return values.map((v) => String(v)).join('|');
}

/** Normalise a key argument: a single id string, or the key values in key-column order. */
export function keyValues(meta: TableMeta, key: string | readonly SqlValue[]): SqlValue[] {
  const values = typeof key === 'string' ? [key] : [...key];
  if (values.length !== meta.key.length) {
    throw new DigesterError('internal', `${meta.table}: key needs ${meta.key.length} value(s)`);
  }
  return values;
}

/** `a = ? AND b = ?` over the key columns. */
export function keyWhere(meta: TableMeta): string {
  return meta.key.map((c) => `${c} = ?`).join(' AND ');
}

/**
 * Tables whose rows the stub cascade physically removes (engine.md §13): every synced table with a parent
 * message — message_topics, message_tags, events, assessments and (data schema 3) message_scores.
 */
export const MESSAGE_CHILD_TABLES: readonly SyncTableName[] = (SYNC_TABLES as readonly SyncTableSpec[])
  .filter((s) => s.parentMessageColumn !== undefined)
  .map((s) => s.table as SyncTableName);

/** LOCAL tables keyed by message that die with it (stub cascade, purge, wipe): the on-device vectors. */
export const MESSAGE_LOCAL_CHILD_TABLES = ['message_vectors'] as const;

/** Tables whose writes change message lists and counts (they bump the data generation). */
export const MESSAGE_DATA_TABLES: ReadonlySet<string> = new Set(['messages', 'message_topics', 'message_tags', 'message_scores']);
